import { reaction } from 'mobx';
import React from 'react';
import { useSelector } from 'react-redux';
import { FB, FBSchemaProps } from '..';
import { companySelectors } from '../../../state/ducks/company';
import FBDeviationProcedureState from './FBDeviationProcedure.state';
import {
  FBDeviationEditorConfig,
  FBDeviationProcedureProps,
  FBDeviationStepValue,
  isDeviationProcedureStep,
} from './FBDeviationProcedure.types';

export const withFBDeviationProcedure = <T extends FBDeviationProcedureProps>(
  Component: React.FunctionComponent<T>,
) => {
  const Comp = ({
    deviationProcedureState,
    name = '',
    isInputOwner,
    isOutput,
    isPreview,
    disabled,
    index = 0,
    ...props
  }: T) => {
    const { workspaceState, formState } = FB.useStores();
    const schemaItem = workspaceState?.getSchemaItemAt(index);
    const redlineActive = useSelector(companySelectors.getRedlineActive);

    // Merge formInput data (source of truth) over editorConfig (may be stale).
    // Computed once on mount so FB.useRef receives the correct initial config.
    const initialConfig: FBDeviationEditorConfig = React.useMemo(() => {
      const baseCfg: FBDeviationEditorConfig = schemaItem?.editorConfig ?? {};
      const formInputSteps = (formState?.getValues()?.[name] ?? {}) as Record<string, Record<string, FBDeviationStepValue>>;
      // Components added in the deviation are defined in formInput (editorConfig is regenerated from the MPI)
      const added = (formInputSteps as unknown as { added_components?: Record<string, Record<string, FBSchemaProps>> })
        .added_components ?? {};
      const editorCfg: FBDeviationEditorConfig = { ...baseCfg };
      Object.entries(added).forEach(([mpiId, items]) => {
        editorCfg[mpiId] = { ...editorCfg[mpiId] };
        Object.entries(items).forEach(([stepId, item]) => {
          editorCfg[mpiId][stepId] = { type: item.type, label: item.label as string, schemaItem: item, added: true };
        });
      });
      if (!Object.keys(formInputSteps).length) { return editorCfg; }
      return Object.fromEntries(
        Object.entries(editorCfg).map(([mpiId, steps]) => [
          mpiId,
          Object.fromEntries(
            Object.entries(steps as Record<string, FBDeviationStepValue>).map(([stepId, stepData]) => {
              const fStep = formInputSteps[mpiId]?.[stepId];
              if (!fStep) { return [stepId, stepData]; }
              // Non-procedure component: formInput holds the control's own value
              if (!isDeviationProcedureStep(stepData)) { return [stepId, { ...stepData, value: fStep }]; }
              return [stepId, {
                ...stepData,
                description: fStep.description ?? stepData.description,
                materials: fStep.materials ?? stepData.materials,
                equipment: fStep.equipment ?? stepData.equipment,
                attachments: fStep.attachments ?? stepData.attachments,
              }];
            }),
          ),
        ]),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // compute once on mount — schemaItem and formState are stable at initial load

    deviationProcedureState = FB.useRef(FBDeviationProcedureState, initialConfig);

    isOutput = Boolean(workspaceState?.isOutput && workspaceState?.id);
    isPreview = (isOutput || workspaceState?.mode === 'preview' || workspaceState?.mode === 'formPreview')
      && !redlineActive;

    isInputOwner = workspaceState?.getIsInputOwner(name);
    disabled = (!isInputOwner || isPreview) && !redlineActive;
    const canEdit = Boolean(isInputOwner) && !['preview', 'formPreview'].includes(workspaceState?.mode as string);

    // value of a non-procedure component lives in formState at stepKey; mirror it into editorConfig
    const boundValues = React.useRef(new Set<string>());
    const valueDisposers = React.useRef<Array<() => void>>([]);
    const bindValue = (mpiId: string, stepId: string) => {
      const stepKey = `${name}.${mpiId}.${stepId}`;
      if (boundValues.current.has(stepKey)) { return; }
      boundValues.current.add(stepKey);
      valueDisposers.current.push(reaction(
        () => formState?.inputState.get(stepKey)?.value,
        (value) => {
          deviationProcedureState?.updateStep(mpiId, stepId, { value } as FBDeviationStepValue);
          formState?.setFieldValue(
            `${name}.deviated_lhr_steps`,
            Array.from(deviationProcedureState?.deviatedMpiIds ?? []),
          );
        },
      ));
    };
    React.useEffect(() => () => valueDisposers.current.forEach((d) => d()), []);

    // MARK: @reactions
    React.useEffect(() => reaction(
      () => deviationProcedureState?.editorConfig,
      (data) => {
        const definitions: Record<string, Record<string, unknown>> = {};
        Object.entries(data ?? {}).forEach(([mpiId, steps]) => {
          Object.entries(steps as Record<string, FBDeviationStepValue>).forEach(([stepId, step]) => {
            if (step.added) {
              definitions[mpiId] = definitions[mpiId] ?? {};
              definitions[mpiId][stepId] = step.schemaItem;
            }
          });
        });
        Object.entries(definitions).forEach(([mpiId, items]) => Object.keys(items).forEach((stepId) => bindValue(mpiId, stepId)));
        if (Object.keys(definitions).length || formState?.getFieldValue(`${name}.added_components`)) {
          formState?.setFieldValue(`${name}.added_components`, definitions);
        }
        // Adding a component deviates its MPI: persist that too, or the badge is lost after a reload
        formState?.setFieldValue(
          `${name}.deviated_lhr_steps`,
          Array.from(deviationProcedureState?.deviatedMpiIds ?? []),
        );
        const schemeItem = workspaceState?.getSchemaItemAt(index);
        if (workspaceState && schemeItem) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          workspaceState.schema![index] = { ...schemeItem, editorConfig: data };
          workspaceState.saveDocRev(formState?.getValues());
        }
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), []);

    // Restore deviated mpiIds from persisted formInput on mount
    React.useEffect(() => {
      const savedDeviated = formState?.getFieldValue(`${name}.deviated_lhr_steps`) as string[] | undefined;
      // MPIs with added components are deviated even if an older save didn't list them
      const withAdded = Object.keys((formState?.getFieldValue(`${name}.added_components`) ?? {}) as object);
      const ids = Array.from(new Set([...(savedDeviated ?? []), ...withAdded]));
      if (ids.length) {
        deviationProcedureState?.initDeviatedMpiIds(ids);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync per-step states → parent editorConfig + formState
    React.useEffect(() => {
      const disposers: Array<() => void> = [];
      Object.entries(initialConfig).forEach(([mpiId, steps]) => {
        Object.entries(steps as Record<string, FBDeviationStepValue>).forEach(([stepId, stepData]) => {
          const stepKey = `${name}.${mpiId}.${stepId}`;

          // Non-procedure component: the control reads/writes formState at stepKey; mirror it to editorConfig
          if (!isDeviationProcedureStep(stepData)) {
            if (stepData.value !== undefined && formState?.getFieldValue(stepKey) === undefined) {
              formState?.setFieldValue(stepKey, stepData.value);
            }
            // Untouched MPI components are read-only clones: binding them would mark the MPI deviated
            // as soon as the control initialises its value (e.g. lhrStepApprovals -> [])
            if (stepData.added) { bindValue(mpiId, stepId); }
            return;
          }

          const stepState = deviationProcedureState?.getStepState(mpiId, stepId);
          if (!stepState) { return; }

          // Seed formState with merged step data on mount
          if (stepData.description && !formState?.getFieldValue(`${stepKey}.description`)) {
            formState?.setFieldValue(`${stepKey}.description`, stepData.description);
          }
          if (stepData.materials) { formState?.setFieldValue(`${stepKey}.materials`, stepData.materials); }
          if (stepData.equipment) { formState?.setFieldValue(`${stepKey}.equipment`, stepData.equipment); }
          if (stepData.attachments) { formState?.setFieldValue(`${stepKey}.attachments`, stepData.attachments); }

          disposers.push(
            reaction(
              () => stepState.value,
              (value) => {
                if (!value) { return; }
                deviationProcedureState?.updateStep(mpiId, stepId, value);
                // Persist deviated mpiIds so the badge survives page refresh
                formState?.setFieldValue(
                  `${name}.deviated_lhr_steps`,
                  Array.from(deviationProcedureState?.deviatedMpiIds ?? []),
                );
                if (value.materials !== undefined) {
                  formState?.setFieldValue(`${stepKey}.materials`, value.materials);
                }
                if (value.equipment !== undefined) {
                  formState?.setFieldValue(`${stepKey}.equipment`, value.equipment);
                }
                if (value.attachments !== undefined) {
                  formState?.setFieldValue(`${stepKey}.attachments`, value.attachments);
                }
              },
            ),
          );
        });
      });
      return () => disposers.forEach((d) => d());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return Component({
      ...(props as T),
      deviationProcedureState,
      isPreview,
      isOutput,
      name,
      disabled,
      canEdit,
    });
  };

  Comp.displayName = 'withFBDeviationProcedure';
  return Comp;
};
